import React from 'react';
import {Box, Stack, Typography} from '@mui/material';
import NodeRenderer from './NodeRenderer';
import {apiMetaToHeaderData} from '../../../utils/documentHeaderMeta';
import PreviewDocumentHeader from './PreviewDocumentHeader';
import {MentionHoverDismissProvider} from '../../../components/entityMentions/MentionHoverDismissContext';

// Background/border treatment per diff status, applied to the top-level block wrapper.
// Matches the block-level granularity of services/ydoc-version-manager/pmDiff.js (text-only,
// keyed by attrs.blockId — marks/formatting changes are not distinguished from unchanged text).
const DIFF_STATUS_SX = {
    added: {
        backgroundColor: 'rgba(46, 125, 50, 0.08)',
        borderLeft: '3px solid',
        borderColor: 'success.main',
        pl: 1,
        ml: -1,
    },
    removed: {
        backgroundColor: 'rgba(198, 40, 40, 0.08)',
        borderLeft: '3px solid',
        borderColor: 'error.main',
        pl: 1,
        ml: -1,
        textDecoration: 'line-through',
        opacity: 0.85,
    },
    changed: {
        backgroundColor: 'rgba(245, 124, 0, 0.1)',
        borderLeft: '3px solid',
        borderColor: 'warning.main',
        pl: 1,
        ml: -1,
    },
};

/**
 * Component to render the main document content
 */
const ReportContent = ({
                           content,
                           meta,
                           documentHeader,
                           previewAuthors,
                           reportDoc,
                           version,
                           latestVersion,
                           referencesByMarkId,
                           referenceNumbers,
                           getReferenceNumber,
                           selectedAuthorId,
                           onAuthorClick,
                           onUserMentionClick,
                           onHashtagClick,
                           afterHeader = null,
                           diffMap = null,
                       }) => {

    if (!content || content.type !== 'doc' || !Array.isArray(content.content)) {
        return null;
    }

    const resolvedHeader = documentHeader ?? apiMetaToHeaderData(meta);

    return (
        <MentionHoverDismissProvider>
        <Stack>
            <Typography
                variant="h4"
                component="h1"
                data-report-scroll-skip
                sx={{fontWeight: 900, letterSpacing: 1, textTransform: 'uppercase'}}
            >
                {reportDoc?.title || 'Untitled Report'}
            </Typography>

            <PreviewDocumentHeader
                headerData={resolvedHeader}
                icrNumber={meta?.icrNumber}
                reportId={reportDoc?.id || meta?.reportId}
                authors={previewAuthors}
                selectedAuthorId={selectedAuthorId}
                onAuthorClick={onAuthorClick}
            />

            {afterHeader}

            {/* Excerpt scroll/highlight only targets body prose — never title/header/chrome. */}
            <Box data-report-excerpt-body>
                {content.content.map((node, i) => {
                    const blockId = node?.attrs?.blockId || node?.attrs?.uid || node?.attrs?.id || node?.id;
                    const diffStatus = diffMap && blockId ? diffMap.get(blockId) : null;
                    const rendered = (
                        <NodeRenderer
                            key={blockId || i}
                            node={node}
                            idx={i}
                            referencesByMarkId={referencesByMarkId}
                            referenceNumbers={referenceNumbers}
                            getReferenceNumber={getReferenceNumber}
                            selectedAuthorId={selectedAuthorId}
                            onUserMentionClick={onUserMentionClick}
                            onHashtagClick={onHashtagClick}
                        />
                    );
                    if (!diffStatus) {
                        return rendered;
                    }
                    return (
                        <Box key={blockId || i} sx={DIFF_STATUS_SX[diffStatus]}>
                            {rendered}
                        </Box>
                    );
                })}
            </Box>
        </Stack>
        </MentionHoverDismissProvider>
    );
};

export default ReportContent;

